import { useCallback, useEffect, useRef } from "react"
import type { MutableRefObject } from "react"
import type { WebViewerInstance } from "@pdftron/webviewer"
import AnnotationService from "services/annotations.service"
import { useMessage } from "hooks/message.hook"
import { useSplitScreenStore } from "store/splitScreen.store"
import type { TViewerMode } from "store/viewer.store"
import { COMPARE_DISABLED_ELEMENTS } from "../core/viewerModes"
import { isPopOutWindowRoute } from "../core/popOutWindow.helpers"

// Split-screen / side-by-side view for the quotation editor — REQ-399 (demo).
//
// Uses Apryse's native MultiViewerMode: TWO DocumentViewers inside the ONE shared WebViewer
// instance, so the single-instance invariant (#1119 — a second `WebViewer` permanently breaks
// click-to-select in the first) still holds. The second viewer loads the SAME document and gets
// its own scroll position, zoom level and annotation manager — its own independent Document/worker
// instance, not literally the primary's Core.Document object (see the comment on the load call in
// enterSplit: sharing that object corrupts rendering when the mode exits). It loads from the same
// already-fetched bytes as the primary (documentBlobRef, from useQuotationViewer), not fileLink
// directly, so entering split screen doesn't re-download a large PDF a second time (PR #1347
// review).
//
// The detection layer is mirrored into the second viewer's manager by AnnotationService
// (registerSplitViewer); both layers are projections of the one TanStack cache, so the
// version-mismatch invariant is inherited — a coordinate edit in either pane reads its version
// from the cache and commits through the same per-entity mutation queue.
//
// Entering/exiting the mode makes Apryse RE-MOUNT the document area, so every DOM node captured
// earlier (above all `documentViewer.getScrollViewElement()`) becomes detached and the handlers
// bound to it go silent. That is what `layoutEpoch` in splitScreen.store is for: bumping it makes
// the orchestrator's listener effects re-bind and the floating overlays re-measure.
//
// Feature flag (plan decision D5): set `VITE_SPLIT_SCREEN=off` to ship the editor without it.
// Exported because the toggle BUTTON has to be gated on it too — the custom header's `render()`
// captures its props once, so the button can only be hidden by a value that is constant for the
// session. Anything dynamic (canSplit) has to go through splitScreen.store instead.
export const SPLIT_SCREEN_ENABLED = import.meta.env.VITE_SPLIT_SCREEN !== "off"

// How long to wait for the pane layout to settle before the final re-measure of the overlays.
const LAYOUT_SETTLE_MS = 350

// Ceiling on the enter handshake (MULTI_VIEWER_READY → second document loaded). Without it, an
// Apryse mode change that never reports ready leaves `isSplitPending` true forever — which disables
// the toggle permanently, and the toggle is the ONLY way back out (the panes' own DocumentHeader,
// which carries Apryse's close control, is hidden by our stylesheet).
const ENTER_TIMEOUT_MS = 15000

export type SplitScreenContext = {
	mode: TViewerMode
	isInitialized: boolean
	isDocumentLoaded: boolean
	fileLink?: string
	instanceRef: MutableRefObject<WebViewerInstance | null>
	// The current document's already-fetched bytes (see useQuotationViewer.tsx), reused for the
	// second viewer's load instead of re-fetching `fileLink` over the network. Null while the fetch
	// is still in flight or failed — enterSplit falls back to `fileLink` in that case.
	documentBlobRef: MutableRefObject<Blob | null>
}

export const useSplitScreen = ({
	mode,
	isInitialized,
	isDocumentLoaded,
	fileLink,
	instanceRef,
	documentBlobRef,
}: SplitScreenContext) => {
	const isSplitActive = useSplitScreenStore((state) => state.isSplitActive)
	const isSplitPending = useSplitScreenStore((state) => state.isSplitPending)
	const setSplitActive = useSplitScreenStore((state) => state.setSplitActive)
	const setSplitPending = useSplitScreenStore((state) => state.setSplitPending)
	const setSplitAvailable = useSplitScreenStore((state) => state.setSplitAvailable)
	const setAutoOpenFloorPlan = useSplitScreenStore((state) => state.setAutoOpenFloorPlan)
	const bumpLayoutEpoch = useSplitScreenStore((state) => state.bumpLayoutEpoch)
	const { error: showError } = useMessage()
	// The MULTI_VIEWER_READY listener is one-shot per enter; kept in a ref so exit/teardown can
	// detach it if the second document never finishes loading.
	const readyListenerRef = useRef<(() => void) | null>(null)
	const relayoutTimersRef = useRef<{ raf: number; timer: number } | null>(null)
	// Watchdog for the enter handshake — see ENTER_TIMEOUT_MS.
	const enterTimeoutRef = useRef<number | null>(null)

	// Announce that the viewer's DOM has been re-laid-out. Bumped on the next frame (Apryse's own
	// re-render must commit first, otherwise the re-bind would capture the OLD element again) and
	// once more after the layout settles, since the pane split animates its width.
	const scheduleRelayout = useCallback(() => {
		if (relayoutTimersRef.current) {
			cancelAnimationFrame(relayoutTimersRef.current.raf)
			window.clearTimeout(relayoutTimersRef.current.timer)
		}
		const raf = requestAnimationFrame(() => bumpLayoutEpoch())
		const timer = window.setTimeout(() => bumpLayoutEpoch(), LAYOUT_SETTLE_MS)
		relayoutTimersRef.current = { raf, timer }
	}, [bumpLayoutEpoch])

	const detachReadyListener = useCallback(() => {
		const instance = instanceRef.current
		if (instance && readyListenerRef.current) {
			instance.UI.removeEventListener(instance.UI.Events.MULTI_VIEWER_READY, readyListenerRef.current)
		}
		readyListenerRef.current = null
	}, [instanceRef])

	const clearEnterWatchdog = useCallback(() => {
		if (enterTimeoutRef.current == null) return
		window.clearTimeout(enterTimeoutRef.current)
		enterTimeoutRef.current = null
	}, [])

	const exitSplit = useCallback(() => {
		clearEnterWatchdog()
		detachReadyListener()
		// Drop the mirrored annotations + listeners BEFORE the second viewer goes away, so we never
		// hold references into a torn-down manager.
		AnnotationService.unregisterSplitViewer()
		try {
			instanceRef.current?.UI.exitMultiViewerMode()
		} catch (error) {
			console.warn("Failed to exit split-screen mode:", error)
		}
		setSplitActive(false)
		setSplitPending(false)
		// Back to one full-width pane — everything bound to the split layout must re-bind.
		scheduleRelayout()
	}, [clearEnterWatchdog, detachReadyListener, instanceRef, scheduleRelayout, setSplitActive, setSplitPending])

	const enterSplit = useCallback(() => {
		const instance = instanceRef.current
		if (!instance || !fileLink) return
		const { UI, Core } = instance

		// "Auto open floor plan" starts ON for every entry into dual screen (#1383). #1311 D4 shipped
		// it OFF so the default was byte-for-byte the pre-#1311 behaviour, but a second pane whose
		// counterpart does NOT follow the selection is the less useful half of the feature — auto-open
		// is the reason to open the pane at all. Set on EVERY enter, not just the first, so the mode
		// always starts in the same state; a user who turns it off is turning it off for that session
		// of the pane, and gets it back next time they open one.
		setAutoOpenFloorPlan(true)

		setSplitPending(true)

		// Captured BEFORE the mode switch: MultiViewerMode re-mounts the app chrome and re-asserts
		// tool state, so the pane's active tool is restored from here once both viewers are up
		// instead of everyone snapping back to the edit tool.
		const toolNameAtEntry = Core.documentViewer.getToolMode()?.name

		const onReady = () => {
			clearEnterWatchdog()
			detachReadyListener()
			// Re-assert after the mode's own chrome mounts (disabling before entering isn't enough —
			// the compare items are registered as the second viewer comes up).
			UI.disableElements(COMPARE_DISABLED_ELEMENTS)
			// Defensive re-assert of the editor's custom header + tool mode: MultiViewerMode
			// re-mounts the app chrome, and if it restored Apryse's default header the split toggle
			// (our only way back out) would disappear. Idempotent — matches what
			// applyViewerHeaderAndTools does for editor mode.
			UI.disableElements(["default-top-header", "tools-header", "page-nav-floating-header", "page-controls-container"])
			UI.enableElements(["custom-top-header"])
			const primaryViewer = Core.documentViewer
			primaryViewer.setToolMode(
				(toolNameAtEntry ? primaryViewer.getTool(toolNameAtEntry) : null) ??
					primaryViewer.getTool(Core.Tools.ToolNames.EDIT),
			)

			const secondViewer = Core.getDocumentViewers()[1]
			if (!secondViewer) {
				// Ready fired but Apryse never produced a second viewer — undo the half-entered mode
				// rather than leaving the user in a split with nothing in the right pane.
				console.error("Split screen: MultiViewerMode reported ready without a second viewer")
				showError("Couldn't open split screen. Please try again.")
				exitSplit()
				return
			}

			// Each viewer loads its OWN independent Document instance — tried making the second viewer
			// share the PRIMARY viewer's Core.Document object instead (an earlier attempt at the same
			// PR #1347 review complaint below): it also skipped the re-download, but exiting split
			// screen afterwards (through ANY exit path, including the pre-existing `Single Page` row)
			// then left the surviving pane's canvas fully blank — confirmed live by reverting and
			// retesting. Sharing the Document object evidently lets Apryse's MultiViewerMode teardown
			// corrupt (or dispose) state the surviving viewer still needs. Don't reintroduce that.
			//
			// What actually fixes the re-download (PR #1347 review: entering split screen re-fetched
			// the whole, often multi-megabyte PDF a second time — "considerable delay" even at 15MB):
			// load from the bytes useQuotationViewer already fetched for the primary viewer, via
			// documentBlobRef, instead of fileLink. Apryse's loadDocument accepts a Blob directly and
			// still gives this viewer its own independent Document/worker — only the NETWORK fetch is
			// shared, not the parsed object — so it carries none of the risk above. Falls back to the
			// URL if the blob isn't ready yet (fetch still in flight, or it failed) — a document isn't
			// worth failing the split-screen enter over.
			secondViewer
				// `extension` matters for the Blob branch: a Blob carries no name, and without it
				// Apryse only assumes "pdf" (and warns). Harmless on the URL branch — this flow is
				// always a PDF.
				.loadDocument(documentBlobRef.current ?? fileLink, { extension: "pdf" })
				.then(() => {
					// Adopt whatever tool the primary pane is on, so opening the split while panning or
					// zooming doesn't leave pane 2 disagreeing with the header. Resolved BY NAME from the
					// second viewer — tool instances are per-DocumentViewer (see core/viewerTools). The
					// ruler is primary-pane only, so pane 2 falls back to the edit tool, which is also
					// what lets clicks select highlights and drag/resize them (REQ-350, second pane).
					const primaryToolName = primaryViewer.getToolMode()?.name
					const isMeasuring =
						primaryViewer.getToolMode() === primaryViewer.getTool(Core.Tools.ToolNames.DISTANCE_MEASUREMENT)
					secondViewer.setToolMode(
						(!isMeasuring && primaryToolName ? secondViewer.getTool(primaryToolName) : null) ??
							secondViewer.getTool(Core.Tools.ToolNames.EDIT),
					)
					// Mirror the detection layer + attach the change listener that commits pane-2 drags.
					AnnotationService.registerSplitViewer(secondViewer)
					setSplitActive(true)
					// Both panes are now half-width and freshly mounted: re-bind + re-measure.
					scheduleRelayout()
				})
				.catch((error) => {
					console.error("Failed to load the document into the split-screen pane:", error)
					showError("Couldn't load the drawing into the split-screen pane.")
					exitSplit()
				})
				.finally(() => setSplitPending(false))
		}

		readyListenerRef.current = onReady
		UI.addEventListener(UI.Events.MULTI_VIEWER_READY, onReady)
		UI.disableElements(COMPARE_DISABLED_ELEMENTS)
		// Armed BEFORE entering: `enterMultiViewerMode` is the call that may never report ready.
		clearEnterWatchdog()
		enterTimeoutRef.current = window.setTimeout(() => {
			enterTimeoutRef.current = null
			console.error("Split screen: MultiViewerMode did not become ready in time")
			showError("Split screen took too long to open. Please try again.")
			exitSplit()
		}, ENTER_TIMEOUT_MS)

		try {
			UI.enterMultiViewerMode()
		} catch (error) {
			// Without this the pending flag set above would stick and disable the toggle for good.
			console.error("Failed to enter split-screen mode:", error)
			showError("Couldn't open split screen. Please try again.")
			exitSplit()
		}
	}, [
		clearEnterWatchdog,
		detachReadyListener,
		documentBlobRef,
		exitSplit,
		fileLink,
		instanceRef,
		scheduleRelayout,
		setAutoOpenFloorPlan,
		setSplitActive,
		setSplitPending,
		showError,
	])

	// `!isPopOutWindowRoute()` is the whole of "the dual view mode must be disabled in the new
	// window" (#1430): with `canSplit` false the store's `isSplitAvailable` is false in there, which
	// disables the menu's `Dual view` row, and `setSplit(true)` returns early — so neither the row,
	// nor the `D` hotkey, nor any future caller can enter a split inside the pop-out. Stating it here
	// rather than at each call site is what makes it an invariant instead of three guards that can
	// drift. The route is constant for that window's life, so this never flips mid-session.
	const canSplit =
		SPLIT_SCREEN_ENABLED && !isPopOutWindowRoute() && mode === "editor" && isInitialized && isDocumentLoaded

	// Published so the header's toggle can disable itself before the document is ready. It cannot be
	// a prop: the header's `render()` captures its props once (see splitScreen.store).
	useEffect(() => {
		setSplitAvailable(canSplit)
	}, [canSplit, setSplitAvailable])

	// Drive split screen to an explicit state. The ONE entry point for both callers — the layout
	// menu's `Single Page` / `Side-by-side` rows (decision D10) and `toggleSplit` below — so the
	// pending guard, the availability check and the enter/exit handshake can never be duplicated or
	// diverge. Idempotent: asking for the state it is already in is a no-op, which is what makes
	// clicking the already-checked layout row harmless.
	const setSplit = useCallback(
		(active: boolean) => {
			if (isSplitPending) return
			if (active === isSplitActive) return
			if (!active) {
				exitSplit()
				return
			}
			if (!canSplit) return
			enterSplit()
		},
		[canSplit, enterSplit, exitSplit, isSplitActive, isSplitPending],
	)

	const toggleSplit = useCallback(() => setSplit(!isSplitActive), [setSplit, isSplitActive])

	// Snapshot one viewer's page/zoom/scroll and re-apply it to the SURVIVING (primary,
	// single-pane) viewer once exitMultiViewerMode's layout has settled. Shared by
	// closeLeftView/closeRightView below — see their own comments for why each needs this rather
	// than trusting exitMultiViewerMode's own collapse target. Order matters on the way back:
	// page, then zoom, then scroll offsets last, since either of the first two can reset the
	// scroll position on its own.
	const restoreViewportAfterExit = useCallback(
		(page: number, zoom: number | undefined, scrollLeft: number | undefined, scrollTop: number | undefined) => {
			exitSplit()
			window.setTimeout(() => {
				const primaryViewer = instanceRef.current?.Core.documentViewer
				if (!primaryViewer) return
				if (page > 0) primaryViewer.setCurrentPage(page, false)
				if (typeof zoom === "number" && zoom > 0) primaryViewer.zoomTo(zoom)
				const primaryScrollEl = primaryViewer.getScrollViewElement?.() as HTMLElement | null
				if (primaryScrollEl) {
					if (typeof scrollLeft === "number") primaryScrollEl.scrollLeft = scrollLeft
					if (typeof scrollTop === "number") primaryScrollEl.scrollTop = scrollTop
				}
			}, LAYOUT_SETTLE_MS)
		},
		[exitSplit, instanceRef],
	)

	// "Close left view" (split-view menu, decision D3 follow-up): discards the left pane and keeps
	// looking at what the right pane was showing. There is no Apryse concept of exiting
	// MultiViewerMode while keeping viewer 2 instead of viewer 1 — so this snapshots viewer 2's
	// page/zoom/scroll BEFORE exiting and re-applies it to the surviving viewer afterwards.
	const closeLeftView = useCallback(() => {
		const instance = instanceRef.current
		const secondViewer = instance?.Core.getDocumentViewers()[1]
		if (!isSplitActive || !secondViewer) {
			exitSplit()
			return
		}
		const page = secondViewer.getCurrentPage()
		const zoom = secondViewer.getZoomLevel?.()
		const secondScrollEl = secondViewer.getScrollViewElement?.() as HTMLElement | null
		restoreViewportAfterExit(page, zoom, secondScrollEl?.scrollLeft, secondScrollEl?.scrollTop)
	}, [exitSplit, instanceRef, isSplitActive, restoreViewportAfterExit])

	// "Close right view" (PR #1347 review — reported broken): discards the right pane and keeps
	// looking at what the LEFT pane was showing. This used to just call exitSplit() on the
	// assumption that exitMultiViewerMode() naturally collapses onto viewer 1 with its viewport
	// intact. That assumption was never verified against live Apryse behaviour and the bug report
	// says it doesn't hold — so, symmetrically with closeLeftView above, this snapshots viewer 1's
	// OWN page/zoom/scroll before exiting and re-applies it once the single-pane layout has
	// settled, rather than trusting whatever exitMultiViewerMode leaves behind.
	const closeRightView = useCallback(() => {
		const instance = instanceRef.current
		const primaryViewer = instance?.Core.documentViewer
		if (!isSplitActive || !primaryViewer) {
			exitSplit()
			return
		}
		const page = primaryViewer.getCurrentPage()
		const zoom = primaryViewer.getZoomLevel?.()
		const primaryScrollEl = primaryViewer.getScrollViewElement?.() as HTMLElement | null
		restoreViewportAfterExit(page, zoom, primaryScrollEl?.scrollLeft, primaryScrollEl?.scrollTop)
	}, [exitSplit, instanceRef, isSplitActive, restoreViewportAfterExit])

	// Split screen is an editor affordance — leaving editor mode (switching to preview) or closing
	// the document must return to the single-pane view first, or the second viewer would linger
	// with a stale mirrored layer.
	useEffect(() => {
		if (!isSplitActive) return
		if (mode !== "editor" || !isDocumentLoaded) exitSplit()
	}, [exitSplit, isDocumentLoaded, isSplitActive, mode])

	// Unmount (modal closed / viewer disposed): detach from Apryse but leave the instance alone —
	// the orchestrator's own cleanup calls UI.dispose() right after.
	useEffect(() => {
		return () => {
			if (relayoutTimersRef.current) {
				cancelAnimationFrame(relayoutTimersRef.current.raf)
				window.clearTimeout(relayoutTimersRef.current.timer)
				relayoutTimersRef.current = null
			}
			if (enterTimeoutRef.current != null) {
				window.clearTimeout(enterTimeoutRef.current)
				enterTimeoutRef.current = null
			}
			AnnotationService.unregisterSplitViewer()
			useSplitScreenStore.setState({
				isSplitActive: false,
				isSplitPending: false,
				isSplitAvailable: false,
				// Smart navigation (#1311) is opt-in per editor session, so the toggle and the
				// Primary/Secondary state go back to their defaults with the viewer.
				isAutoOpenFloorPlan: false,
				primaryPane: "primary",
			})
		}
	}, [])

	// The per-pane canvas handlers (mousedown hit-test bypass, contextmenu, mousemove) are bound by
	// the orchestrator across BOTH panes, keyed on layoutEpoch — see useQuotationViewer.

	return { isSplitActive, isSplitPending, canSplit, setSplit, toggleSplit, exitSplit, closeLeftView, closeRightView }
}
